import { shotWarning } from "./damageControlWarnings";

export const ACCEPTED_SHOT_STATES = Object.freeze(["accepted", "accepted_failed", "accepted_unchecked"]);
export const acceptedShot = (shot) => Boolean(shot && ACCEPTED_SHOT_STATES.includes(shot.state));
export const REVIEW_OUTCOME_KEYS = Object.freeze({
  new_damage: "damage_control.review.outcomes.new_damage",
  no_new_damage: "damage_control.review.outcomes.no_new_damage",
});

export function acceptedCurrentShot(zone) {
  const shot = zone && zone.current_shot;
  return Boolean(acceptedShot(shot) && shot.current !== false && !shot.superseded_by_id);
}

export function zoneReviewState(zone, side) {
  const state = { available: false, reason: null, checked: false, outcome: null };
  if (side !== "arrival") return state;
  const shot = zone && zone.current_shot;
  if (!acceptedCurrentShot(zone)) return { ...state, reason: "damage_control.review.missing_photo" };
  const review = zone.zone_review;
  state.checked = Boolean(review && review.shot_id === shot.id);
  state.outcome = state.checked ? review.outcome : null;
  const warning = shotWarning(shot);
  if (shot.vision_stage === "pending") state.reason = "damage_control.review.pending";
  else if (warning && !["drawn", "dismissed"].includes(warning.resolution)) state.reason = "damage_control.review.warning_unresolved";
  else if ((shot.findings || []).some(finding => finding.stale_known)) state.reason = "damage_control.review.stale";
  else state.available = true;
  return state;
}

// Each blocker identifies its zone and a literal translation key, shared with STEP-24.
// Warnings only matter through mandatory arrival reviews; optional/departure ones never gate completion.
export function completionBlockers(inspection) {
  if (!inspection) return [];
  return (inspection.zones || []).flatMap(zone => {
    const blockers = [];
    const add = reason => blockers.push({ zone_key: zone.key, title: zone.title, reason });
    const shot = zone.current_shot;
    const stale = shot && shot.current !== false && !shot.superseded_by_id && (shot.findings || []).some(finding => finding.stale_known);
    if (zone.required) {
      if (!acceptedCurrentShot(zone)) add("damage_control.review.missing_photo");
      else if (inspection.side === "arrival") {
        const review = zoneReviewState(zone, inspection.side);
        if (!review.available || !review.checked) add(review.reason || "damage_control.review.not_reviewed");
      }
    }
    if (stale && !blockers.some(blocker => blocker.reason === "damage_control.review.stale")) add("damage_control.review.stale");
    return blockers;
  });
}

// WarningBanner uses strict IDs; finding controls also accept serialized IDs.
export function shotStillCurrent(inspection, zoneKey, shot, { coerceId = false } = {}) {
  const zone = (inspection.zones || []).find(item => item.key === zoneKey);
  return Boolean(zone && zone.current_shot && (coerceId
    ? String(zone.current_shot.id) === String(shot.id)
    : zone.current_shot.id === shot.id));
}

export function zoneEntries(entries, zoneKey, t) {
  return entries.filter(entry => entry && entry.status === "active" && entry.active !== false && entry.zone_key === zoneKey)
    .map(entry => ({ ...entry, label: t("damage_control.review.known_record_label", { id: entry.id }) }));
}
